import React, { useState } from 'react';
import { XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useGetUploadUrlMutation } from '../../store/api/localApi';

const MAX_BYTES = 20 * 1024 * 1024; // 20MB — mirrors the server STAFF_CREDENTIAL cap.
const ALLOWED_MIME = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/heic']);

interface Props {
  /** The staff member this credential/vetting document belongs to. Used as the
   *  presign `contextId` — the server namespaces the S3 key by org + this id,
   *  so there is no client-controlled key. */
  staffUserId: string;
  /** Called with the uploaded object's server-returned s3Key once a PUT
   *  succeeds, or `undefined` when the selection is cleared. The parent form
   *  includes it as `attachmentS3Key` on submit only when set. */
  onChange: (s3Key: string | undefined) => void;
  /** Called whenever an upload starts/finishes so the parent can disable Save
   *  while a PUT is in flight (mirrors ObservationCreateModal's anyUploading). */
  onUploadingChange: (uploading: boolean) => void;
  /** testid prefix — the file input gets this, and derived controls append a
   *  suffix. e.g. `vetting-attachment-input` / `credential-attachment-input`. */
  inputTestId: string;
}

/**
 * Single-file document attachment for a staff-compliance record (Garda vetting
 * disclosure PDF, qualification cert, …). Mirrors the presign → PUT → keep-s3Key
 * pattern used by ObservationCreateModal, but for ONE file rather than a photo
 * array: a credential attaches a single document.
 *
 * The flow: presign(purpose STAFF_CREDENTIAL, contextId=staffUserId) → PUT the
 * raw File to the returned S3 URL → hand the returned s3Key up via onChange.
 * The file bytes never pass through the API — the PUT goes straight to S3.
 */
const CredentialAttachmentInput: React.FC<Props> = ({
  staffUserId,
  onChange,
  onUploadingChange,
  inputTestId,
}) => {
  const [getUploadUrl] = useGetUploadUrlMutation();
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setUploadingState = (next: boolean) => {
    setUploading(next);
    onUploadingChange(next);
  };

  const clear = () => {
    setFileName(null);
    setError(null);
    setUploadingState(false);
    onChange(undefined);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    onChange(undefined);

    if (!ALLOWED_MIME.has(file.type)) {
      setFileName(null);
      setError('Please choose a PDF, JPEG, PNG, or HEIC file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileName(null);
      setError('That file is too large. The maximum is 20MB.');
      return;
    }

    setFileName(file.name);
    setUploadingState(true);
    try {
      const { uploadUrl, s3Key } = await getUploadUrl({
        purpose: 'STAFF_CREDENTIAL',
        contextId: staffUserId,
        mimeType: file.type,
        sizeBytes: file.size,
        fileName: file.name,
      }).unwrap();
      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putResp.ok) throw new Error(`S3 ${putResp.status}`);
      onChange(s3Key);
    } catch {
      setError('Upload failed. Please try again.');
      setFileName(null);
      onChange(undefined);
    } finally {
      setUploadingState(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        Document
        <span className="ml-1 text-xs font-normal text-gray-500">
          (optional — PDF, JPEG, PNG, or HEIC, up to 20MB)
        </span>
      </label>

      {fileName ? (
        <div className="mt-1 flex items-center gap-2 text-sm text-gray-700">
          <PaperClipIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          <span className="truncate" data-testid={`${inputTestId}-filename`}>
            {fileName}
          </span>
          {uploading ? (
            <span className="text-xs text-gray-500" data-testid={`${inputTestId}-uploading`}>
              Uploading…
            </span>
          ) : (
            <button
              type="button"
              data-testid={`${inputTestId}-remove`}
              onClick={clear}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Remove attachment"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <input
          type="file"
          data-testid={inputTestId}
          accept="application/pdf,image/*"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
        />
      )}

      {error && (
        <p className="mt-1 text-xs text-red-600" data-testid={`${inputTestId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default CredentialAttachmentInput;
